import React, { useState, useEffect } from 'react';
import { Play, Pause, Square, SkipBack, SkipForward, Calendar } from 'lucide-react';
import { TraccarTrip, Vehicle } from '../../types';
import { traccarApi, vehicleApi } from '../../services/api';
import LoadingSpinner from '../ui/LoadingSpinner';

const TripReplay: React.FC = () => {
  const [trips, setTrips] = useState<TraccarTrip[]>([]);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedTrip, setSelectedTrip] = useState<TraccarTrip | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [replayProgress, setReplayProgress] = useState(0);
  const [replaySpeed, setReplaySpeed] = useState(1);
  const [dateFilter, setDateFilter] = useState(new Date().toISOString().split('T')[0]);

  useEffect(() => {
    fetchVehicles();
    fetchTrips();
  }, [dateFilter]);

  const fetchVehicles = async () => {
    try {
      const response = await vehicleApi.getAll();
      if (response.success && response.data) {
        setVehicles(response.data);
      }
    } catch (err) {
      setError('Failed to fetch vehicles');
    }
  };

  const fetchTrips = async () => {
    try {
      setLoading(true);
      
      // Fetch trips for the selected date
      const fromDate = new Date(dateFilter + 'T00:00:00').toISOString();
      const toDate = new Date(dateFilter + 'T23:59:59').toISOString();
      
      // For demo, we'll fetch trips from all devices
      const allTrips: TraccarTrip[] = [];
      
      for (const vehicle of vehicles) {
        if (vehicle.traccar_device_id) {
          try {
            const response = await traccarApi.getTrips(vehicle.traccar_device_id, fromDate, toDate);
            if (response.success && response.data) {
              const vehicleTrips = response.data.map(trip => ({
                ...trip,
                deviceName: vehicle.name
              }));
              allTrips.push(...vehicleTrips);
            }
          } catch (err) {
            console.warn(`Failed to fetch trips for vehicle ${vehicle.name}`);
          }
        }
      }
      
      setTrips(allTrips);
    } catch (err) {
      setError('Failed to fetch trips');
    } finally {
      setLoading(false);
    }
  };

  const handlePlayPause = () => {
    setIsPlaying(!isPlaying);
  };

  const handleStop = () => {
    setIsPlaying(false);
    setReplayProgress(0);
  };

  const handleSpeedChange = (speed: number) => {
    setReplaySpeed(speed);
  };

  const formatDuration = (milliseconds: number) => {
    const minutes = Math.floor(milliseconds / 60000);
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    
    if (hours > 0) {
      return `${hours}h ${remainingMinutes}m`;
    }
    return `${remainingMinutes}m`;
  };

  const formatDistance = (meters: number) => {
    if (meters >= 1000) {
      return `${(meters / 1000).toFixed(1)} km`;
    }
    return `${Math.round(meters)} m`;
  };

  const getVehicleName = (deviceName: string) => {
    return deviceName || 'Unknown Vehicle';
  };

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* Header with Date Filter */}
      <div className="flex items-center justify-between p-4 bg-white border-b">
        <div className="flex items-center space-x-4">
          <h2 className="text-lg font-semibold text-fleet-dark">Trip Replay</h2>
          <div className="flex items-center space-x-2">
            <Calendar className="h-4 w-4 text-fleet-neutral" />
            <input
              type="date"
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              className="border border-gray-300 rounded px-2 py-1 text-sm"
            />
          </div>
        </div>
        
        <div className="text-sm text-fleet-neutral">
          {trips.length} trip{trips.length !== 1 ? 's' : ''} found
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="m-4 p-3 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-700 text-sm">{error}</p>
        </div>
      )}

      <div className="flex-1 flex">
        {/* Trip List */}
        <div className="w-1/3 border-r bg-white">
          <div className="p-4 border-b">
            <h3 className="font-medium text-fleet-dark">Available Trips</h3>
          </div>
          
          <div className="overflow-y-auto h-full">
            {trips.length === 0 ? (
              <div className="p-4 text-center text-fleet-neutral">
                No trips found for {dateFilter}
              </div>
            ) : (
              trips.map((trip) => (
                <div
                  key={trip.id}
                  onClick={() => setSelectedTrip(trip)}
                  className={`p-4 border-b cursor-pointer hover:bg-gray-50 ${
                    selectedTrip?.id === trip.id ? 'bg-fleet-primary bg-opacity-10' : ''
                  }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-medium text-fleet-dark">
                      {getVehicleName(trip.deviceName)}
                    </h4>
                    <span className="text-xs text-fleet-neutral">
                      {new Date(trip.startTime).toLocaleTimeString()}
                    </span>
                  </div>
                  
                  <div className="space-y-1 text-sm text-fleet-neutral">
                    <div className="flex justify-between">
                      <span>Duration:</span>
                      <span>{formatDuration(trip.duration)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Distance:</span>
                      <span>{formatDistance(trip.distance)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Avg Speed:</span>
                      <span>{Math.round(trip.averageSpeed)} km/h</span>
                    </div>
                  </div>
                  
                  {trip.startAddress && (
                    <div className="mt-2 text-xs text-fleet-neutral">
                      <div>From: {trip.startAddress}</div>
                      {trip.endAddress && <div>To: {trip.endAddress}</div>}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>

        {/* Replay Controls and Map */}
        <div className="flex-1 flex flex-col">
          {selectedTrip ? (
            <>
              {/* Trip Details Header */}
              <div className="p-4 bg-white border-b">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold text-fleet-dark">
                      {getVehicleName(selectedTrip.deviceName)} - Trip Replay
                    </h3>
                    <p className="text-sm text-fleet-neutral">
                      {new Date(selectedTrip.startTime).toLocaleString()} - {new Date(selectedTrip.endTime).toLocaleString()}
                    </p>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-fleet-neutral">Speed:</span>
                    <select
                      value={replaySpeed}
                      onChange={(e) => handleSpeedChange(Number(e.target.value))}
                      className="border border-gray-300 rounded px-2 py-1 text-sm"
                    >
                      <option value={0.5}>0.5x</option>
                      <option value={1}>1x</option>
                      <option value={2}>2x</option>
                      <option value={5}>5x</option>
                      <option value={10}>10x</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Replay Controls */}
              <div className="p-4 bg-white border-b">
                <div className="flex items-center justify-center space-x-4">
                  <button
                    onClick={() => setReplayProgress(0)}
                    className="p-2 rounded-full bg-gray-200 hover:bg-gray-300"
                  >
                    <SkipBack className="h-4 w-4" />
                  </button>
                  
                  <button
                    onClick={handlePlayPause}
                    className="p-3 rounded-full bg-fleet-primary text-white hover:bg-fleet-secondary"
                  >
                    {isPlaying ? <Pause className="h-5 w-5" /> : <Play className="h-5 w-5" />}
                  </button>
                  
                  <button
                    onClick={handleStop}
                    className="p-2 rounded-full bg-gray-200 hover:bg-gray-300"
                  >
                    <Square className="h-4 w-4" />
                  </button>
                  
                  <button
                    onClick={() => setReplayProgress(100)}
                    className="p-2 rounded-full bg-gray-200 hover:bg-gray-300"
                  >
                    <SkipForward className="h-4 w-4" />
                  </button>
                </div>
                
                {/* Progress Bar */}
                <div className="mt-4">
                  <div className="flex justify-between text-xs text-fleet-neutral mb-1">
                    <span>Start</span>
                    <span>{Math.round(replayProgress)}%</span>
                    <span>End</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-fleet-primary h-2 rounded-full transition-all duration-300"
                      style={{ width: `${replayProgress}%` }}
                    ></div>
                  </div>
                </div>
              </div>

              {/* Map Placeholder */}
              <div className="flex-1 bg-gray-100 flex items-center justify-center">
                <div className="text-center">
                  <div className="text-2xl mb-2">🗺️</div>
                  <h3 className="text-lg font-medium text-fleet-dark mb-2">Map View</h3>
                  <p className="text-fleet-neutral">
                    Interactive map with trip replay would be displayed here
                  </p>
                  <div className="mt-4 p-4 bg-white rounded-lg shadow text-left max-w-md">
                    <h4 className="font-medium mb-2">Trip Statistics:</h4>
                    <div className="space-y-1 text-sm">
                      <div>Total Distance: {formatDistance(selectedTrip.distance)}</div>
                      <div>Duration: {formatDuration(selectedTrip.duration)}</div>
                      <div>Average Speed: {Math.round(selectedTrip.averageSpeed)} km/h</div>
                      <div>Max Speed: {Math.round(selectedTrip.maxSpeed)} km/h</div>
                    </div>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center bg-gray-50">
              <div className="text-center">
                <div className="text-4xl mb-4">🚗</div>
                <h3 className="text-lg font-medium text-fleet-dark mb-2">Select a Trip</h3>
                <p className="text-fleet-neutral">
                  Choose a trip from the list to start replay
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TripReplay;